import { useCallback, useMemo, useState } from "react";

const DEFAULT_ANCHOR_ORIGIN = {
  vertical: "bottom",
  horizontal: "center",
};

const DEFAULT_AUTO_HIDE_DURATION = 4000;
const DEFAULT_ERROR_AUTO_HIDE_DURATION = 7000;

const buildAutoHideDuration = (type, autoHideDuration) => {
  if (typeof autoHideDuration === "number") {
    return autoHideDuration;
  }

  return type === "error"
    ? DEFAULT_ERROR_AUTO_HIDE_DURATION
    : DEFAULT_AUTO_HIDE_DURATION;
};

export const useSnackbar = (initialState = {}) => {
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    type: "success",
    text: "",
    autoHideDuration: DEFAULT_AUTO_HIDE_DURATION,
    anchorOrigin: DEFAULT_ANCHOR_ORIGIN,
    ...initialState,
  });

  const closeSnackbar = useCallback((event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarState((prev) => ({
      ...prev,
      open: false,
      text: "",
    }));
  }, []);

  const showSnackbar = useCallback((text, type = "success", options = {}) => {
    setSnackbarState((prev) => ({
      ...prev,
      open: true,
      text,
      type,
      autoHideDuration: buildAutoHideDuration(type, options.autoHideDuration),
      anchorOrigin: options.anchorOrigin || prev.anchorOrigin,
    }));
  }, []);

  const showSnackbarSuccess = useCallback(
    (text, options = {}) => {
      showSnackbar(text, "success", options);
    },
    [showSnackbar],
  );

  const showSnackbarError = useCallback(
    (text, options = {}) => {
      showSnackbar(text, "error", options);
    },
    [showSnackbar],
  );

  const resetSnackbar = useCallback(() => {
    setSnackbarState((prev) => ({
      ...prev,
      open: false,
      text: "",
    }));
  }, []);

  const snackbarProps = useMemo(
    () => ({
      ...snackbarState,
      onClose: closeSnackbar,
    }),
    [closeSnackbar, snackbarState],
  );

  return {
    snackbarProps,
    showSnackbar,
    showSnackbarSuccess,
    showSnackbarError,
    closeSnackbar,
    resetSnackbar,
  };
};
